import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Input, Space, Select } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { message } from 'antd';
import AxiosOICStat from '../../AxiosOICStat';

const CountryEditModal = ({ visible, record, onCancel, onUpdate }) => {
    const [form] = Form.useForm();
    const [allRegions, setAllRegions] = useState([]);

    useEffect(() => {
        AxiosOICStat.get('countries')
            .then(response => {
                const regions = new Set();
                response.data.forEach(country => {
                    if (country.region_names && Array.isArray(country.region_names)) {
                        country.region_names.forEach(region => regions.add(region));
                    }
                });
                setAllRegions(Array.from(regions).sort());
            })
            .catch(err => console.log(err));
    }, []);

    useEffect(() => {
        if (visible && record) {
            const data = { ...record };
            
            if (data.c_alt_names && Array.isArray(data.c_alt_names)) {
                data.alt_names_list = data.c_alt_names.map(name => ({ name }));
            } else if (data.c_alt_names && typeof data.c_alt_names === 'string') {
                data.alt_names_list = data.c_alt_names.split(',').map(name => ({ name: name.trim() }));
            } else {
                data.alt_names_list = [];
            }
            
            if (data.region_names && Array.isArray(data.region_names)) {
                data.regions_list = data.region_names.map(region => ({ region }));
            } else {
                data.regions_list = [];
            }
            
            form.setFieldsValue(data);
        } else if (!visible) {
            form.resetFields();
        }
    }, [visible, record, form]);

    const handleEditSubmit = (values) => {
        const altNamesArray = values.alt_names_list 
            ? values.alt_names_list.map(item => item.name).filter(name => name && name.length > 0)
            : [];
        
        const regionsArray = values.regions_list
            ? values.regions_list.map(item => item.region).filter(region => region && region.length > 0)
            : [];
        
        const submitData = {
            ...values,
            c_alt_names: altNamesArray,
            region_names: regionsArray,
            alt_names_list: undefined,
            regions_list: undefined
        };

        const lookupId = record.id ?? record.country_id ?? record.c_code;
        AxiosOICStat.put(`countries/${lookupId}`, submitData)
            .then(() => {
                message.success('Country updated successfully');
                onUpdate({...submitData, c_code: record.c_code});
                onCancel();
            })
            .catch(err => {
                console.error('Error updating country:', err);
                message.error('Error updating country');
            });
    };

    return (
        <Modal
            visible={visible}
            title="Edit Country"
            onCancel={onCancel}
            width="50%"
            footer={[
                <Button key="cancel" onClick={onCancel}>Cancel</Button>,
                <Button key="edit" type="primary" onClick={() => form.submit()}>Save</Button>,
            ]}
        >
            {record && (
                <Form form={form} onFinish={handleEditSubmit}>
                    <Form.Item label="Code" name="c_code"><Input disabled /></Form.Item>
                    <Form.Item label="Name" name="c_short_name_eng"><Input /></Form.Item>
                    <Form.Item label="Full Name" name="c_full_name_eng"><Input /></Form.Item>
                    
                    <Form.Item label="Regions">
                        <Form.List name="regions_list">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'region']}
                                                rules={[{ required: false }]}
                                            >
                                                <Select placeholder="Select region" style={{ width: 200 }}>
                                                    {allRegions.map(region => (
                                                        <Select.Option key={region} value={region}>
                                                            {region}
                                                        </Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                            <MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red', cursor: 'pointer' }} />
                                        </Space>
                                    ))}
                                    <Form.Item>
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                            Add Region
                                        </Button>
                                    </Form.Item>
                                </>
                            )}
                        </Form.List>
                    </Form.Item>
                    
                    <Form.Item label="Alternative Names">
                        <Form.List name="alt_names_list">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'name']}
                                                rules={[{ required: false }]}
                                            >
                                                <Input placeholder="Enter alternative name" />
                                            </Form.Item>
                                            <MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red', cursor: 'pointer' }} />
                                        </Space>
                                    ))}
                                    <Form.Item>
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                            Add Alternative Name
                                        </Button>
                                    </Form.Item>
                                </>
                            )}
                        </Form.List>
                    </Form.Item>

                    <Form.Item label="Phone Prefix" name="c_phone_prefix"><Input /></Form.Item>
                    <Form.Item label="Traffic Code" name="c_traffic_code"><Input /></Form.Item>
                </Form>
            )}
        </Modal>
    );
};

export default CountryEditModal;
